import { motion } from "motion/react"
import { useRef } from "react"
import { BubbleTail } from "@/components/ui/bubble-tail"
import { CornerTicks } from "@/components/ui/corner-ticks"
import { DottedGlowBackground } from "@/components/ui/dotted-glow-background"
import { EncryptedText } from "@/components/ui/encrypted-text"
import { Fact } from "@/components/ui/fact"
import { MutuPrompt } from "@/components/ui/mutu-prompt"
import { SectionTitle } from "@/components/ui/section-title"
import { SharedButton } from "@/components/ui/shared-button"
import { TransmissionPanel } from "@/components/ui/transmission-panel"
import { usePinnedReveal } from "@/hooks/use-pinned-reveal"

const QUIP_TEXT =
  "And yes, these are the people who built me. I have unresolved feelings about that."

export function CompanySection() {
  const sectionRef = useRef<HTMLElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const tailRef = useRef<HTMLSpanElement>(null)

  usePinnedReveal({ sectionRef, bubbleRef, bodyRef, tailRef })

  return (
    <section
      ref={sectionRef}
      id="company"
      className="relative z-10 flex min-h-svh w-full flex-col items-start px-5 py-20 sm:px-10 sm:py-28 md:z-auto lg:py-32"
    >
      <div className="relative z-30 order-1 mb-6 flex w-full justify-center md:hidden">
        <SectionTitle prefix="The" accent="Company" />
      </div>

      <motion.div
        initial="hidden"
        whileInView="shown"
        viewport={{ once: true, amount: 0.25 }}
        className="relative z-30 order-3 grid w-full max-w-4xl grid-cols-1 items-center gap-10 sm:gap-12 md:order-1 md:grid-cols-2"
      >
        <div className="flex flex-col">
          <div className="mx-auto hidden w-full max-w-60 justify-center sm:max-w-88 md:flex">
            <SectionTitle prefix="The" accent="Company" />
          </div>
          <div className="md:mt-10">
            <LogoFrame />
          </div>
        </div>
        <Pitch />
      </motion.div>

      <div className="relative z-30 order-2 mb-10 w-full max-w-xl md:absolute md:top-[12%] md:right-10 md:order-2 md:mb-0 md:max-w-80 lg:right-16">
        <div ref={bubbleRef} className="relative">
          <TransmissionPanel>
            <div ref={bodyRef}>
              <MutuPrompt verb="re" subject="SHY Productions" />
              <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.7]">
                <EncryptedText
                  text={QUIP_TEXT}
                  encryptedClassName="text-label-dim"
                  revealedClassName="text-foreground"
                  revealDelayMs={45}
                  flipDelayMs={45}
                />
              </p>
            </div>
          </TransmissionPanel>
          <BubbleTail ref={tailRef} side="bottom" />
        </div>
      </div>
    </section>
  )
}

function LogoFrame() {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        shown: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto w-full max-w-60 sm:max-w-88"
    >
      <div className="relative aspect-square overflow-hidden border border-white/15 bg-background/40 shadow-[0_0_60px_rgb(var(--brand-rgb)/0.08)] backdrop-blur-sm">
        <DottedGlowBackground
          className="pointer-events-none opacity-100"
          style={{
            maskImage:
              "radial-gradient(circle at center, black 35%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(circle at center, black 35%, transparent 90%)",
          }}
          opacity={1}
          gap={10}
          radius={1.8}
          colorDarkVar="--color-sky-400"
          glowColorDarkVar="--color-sky-200"
          backgroundOpacity={0}
          speedMin={0.3}
          speedMax={1.6}
          speedScale={1}
        />
        <div className="absolute inset-0 z-10 flex items-center justify-center p-10">
          <img
            src="https://70fy8xbp6b.ufs.sh/f/BzM3qquY5hLZiF1TDkbdvV6aHBk1WmSs9T8IPn2xYy0ezhjf"
            alt="SHY Productions"
            width={600}
            height={600}
            className="max-h-full max-w-full object-contain"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
        </div>
        <CornerTicks />
      </div>
      <p className="mt-4 text-center font-mono text-[10px] text-label uppercase tracking-[0.4em]">
        SHY Productions · Estd. 2020
      </p>
    </motion.div>
  )
}

function Pitch() {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        shown: { opacity: 1, y: 0 },
      }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="text-center md:text-left"
    >
      <h2 className="font-medium font-mono text-[clamp(1.4rem,2.6vw,2rem)] text-white uppercase leading-[1.15] tracking-[-0.02em]">
        Discover narratives
        <br />
        beyond borders.
      </h2>
      <p className="mt-6 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.75]">
        Fusion of British and Nepalese cultures, unearthing untold stories
        through evocative filmmaking. SHY Productions has spent over a decade
        bridging two cinematic traditions, bringing Himalayan mythology, modern
        sci-fi, and an unmistakably global voice into one universe.
      </p>
      <p className="mt-4 font-mono text-foreground text-sm leading-relaxed sm:text-base sm:leading-[1.75]">
        We build worlds first, then characters who refuse to leave them.
      </p>
      <div className="mt-8 grid grid-cols-2 gap-4 border-white/10 border-t pt-5">
        <Fact label="Origin" value="UK" />
        <Fact label="Genre" value="Multiversal Storytelling" />
      </div>
      <SharedButton
        to="/contact"
        className="mx-auto mt-8 inline-flex w-full max-w-xs justify-center md:mx-0 md:w-fit md:max-w-none"
      >
        Discuss a project
      </SharedButton>
    </motion.div>
  )
}
